/**
 * PURE watch-progress heuristic (F3-F). Feeds the MONOTONIC `watched_pct`
 * of `video_watch_progress` (F2-B helper `upsertWatchProgress`) without
 * letting "skipping to the end" count as watched.
 *
 * Model: it records the set of WHOLE SECONDS the cursor has passed through
 * DURING playback (deltas from `timeupdate`). `watchedPct` =
 * seconds-covered / duration. Skipping forward does NOT fill in the gaps;
 * re-watching a segment doesn't count it twice. `maxPosition` tracks the
 * furthest point reached (for "resume where it left off").
 *
 * It's a flat, deterministic state object → testable without a DOM.
 */

export interface WatchTrackerState {
  /** Whole seconds covered (indices 0..floor(duration)-1). */
  covered: Set<number>;
  /** Furthest point reached (seconds). For resuming. */
  maxPosition: number;
  /** Last observed position (to compute the next tick's delta). */
  lastPosition: number;
  /** Total duration (seconds); 0 until known (metadata). */
  duration: number;
}

/** Maximum gap (seconds) between two ticks still considered continuous
 * playback. Above that it's a SEEK: the in-between seconds are not filled in. */
const CONTINUOUS_TICK_MAX = 2;

export function createWatchTrackerState(duration = 0, initialPct = 0): WatchTrackerState {
  const state: WatchTrackerState = {
    covered: new Set<number>(),
    maxPosition: 0,
    lastPosition: 0,
    duration: Number.isFinite(duration) && duration > 0 ? duration : 0,
  };
  // Seeds the previous progress (monotonic): marks the first N seconds as
  // covered so a synced `watched_pct` doesn't regress on reopening.
  if (initialPct > 0 && state.duration > 0) {
    const seeded = Math.round(clamp01(initialPct) * Math.ceil(state.duration));
    for (let i = 0; i < seeded; i++) {
      state.covered.add(i);
    }
  }
  return state;
}

/** Sets/updates the duration (once known via metadata). */
export function setTrackerDuration(state: WatchTrackerState, duration: number): void {
  if (Number.isFinite(duration) && duration > 0) {
    state.duration = duration;
  }
}

/**
 * Records a playback position (called on every `timeupdate`). If the
 * gap from the last position is continuous (≤ CONTINUOUS_TICK_MAX) it marks
 * the in-between seconds as covered; if it's a seek, it only updates the
 * reference without filling in.
 */
export function trackPosition(state: WatchTrackerState, position: number): void {
  if (!Number.isFinite(position) || position < 0) {
    return;
  }
  const prev = state.lastPosition;
  const delta = position - prev;
  if (delta > 0 && delta <= CONTINUOUS_TICK_MAX) {
    const from = Math.floor(prev);
    const to = Math.floor(position);
    for (let s = from; s <= to; s++) {
      state.covered.add(s);
    }
  } else if (delta >= 0 && delta <= CONTINUOUS_TICK_MAX) {
    // Same second / negligible advance: mark the current second.
    state.covered.add(Math.floor(position));
  }
  // Seek (large or negative delta): doesn't fill in; just a new reference.
  state.lastPosition = position;
  if (position > state.maxPosition) {
    state.maxPosition = position;
  }
}

/** Fraction watched [0,1] based on second coverage. */
export function watchedFraction(state: WatchTrackerState): number {
  if (state.duration <= 0) {
    return 0;
  }
  const total = Math.ceil(state.duration);
  if (total <= 0) {
    return 0;
  }
  return clamp01(state.covered.size / total);
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.min(1, Math.max(0, n));
}
